import { useEffect, useState } from "react";
import { useRecentSamples } from "../api";
import { useParamsContext, useUserContext, useAdminContext } from "../contexts";
import Footer from "../components/Footer";
import Navbar from "../components/SpectraNavbar";
import DateSelector from "../components/controls/DateSelector";
import LocationDropdown from "../components/controls/LocationDropdown";
import Pagination from "../components/controls/Pagination";
import { CardA, CardC } from "../components/ui/Cards";
import { SequenceD } from "../components/ui/Sequence";
import { ColourCircleC } from "../components/ui/ColourCircle";
import { DateTime } from "luxon";
import { CSVLink } from "react-csv";

const headers = {
  "harm-reduction": [
    { label: "Sample ID", key: "sampleId" },
    { label: "Custom ID", key: "customId" },
    { label: "Location", key: "location" },
    { label: "Date", key: "processedDate" },
    { label: "Time", key: "time" },
    { label: "Presumed Substance", key: "presumedSubstance" },
    { label: "Colour", key: "colourName" },
    { label: "Texture", key: "textureName" },
    { label: "Bulk Scan", key: "bulkScanExport" },
    { label: "Trace Scan", key: "traceScanExport" },
    { label: "Adverse Reaction", key: "adverseReaction" },
    { label: "Adverse Reaction Symptoms", key: "adverseReactionSymptoms" },
    {
      label: "Adverse Reaction Intervention",
      key: "adverseReactionIntervention",
    },
    { label: "Scan Timing", key: "tested" },
    { label: "Consumption Location", key: "consumptionLocation" },
    { label: "Behaviour Change", key: "behaviourChange" },
    { label: "Consumption Method", key: "consumptionMethod" },
    { label: "Notes", key: "notes" },
    { label: "Other Analytical Results", key: "otherResults" },
  ],
  "law-enforcement": [
    { label: "Sample ID", key: "sampleId" },
    { label: "Custom ID", key: "customId" },
    { label: "Location", key: "location" },
    { label: "Date", key: "processedDate" },
    { label: "Time", key: "time" },
    { label: "Presumed Substance", key: "presumedSubstance" },
    { label: "Colour", key: "colourName" },
    { label: "Texture", key: "textureName" },
    { label: "Bulk Scan", key: "bulkScanExport" },
    { label: "Trace Scan", key: "traceScanExport" },
    { label: "Notes", key: "notes" },
    { label: "Other Analytical Results", key: "otherResults" },
  ],
  admin: [
    { label: "Sample ID", key: "sampleId" },
    { label: "Custom ID", key: "customId" },
    { label: "Customer Name", key: "customer" },
    { label: "Location", key: "location" },
    { label: "Date", key: "processedDate" },
    { label: "Time", key: "time" },
    { label: "Presumed Substance", key: "presumedSubstance" },
    { label: "Colour", key: "colourName" },
    { label: "Texture", key: "textureName" },
    { label: "Bulk Scan", key: "bulkScanExport" },
    { label: "Trace Scan", key: "traceScanExport" },
    { label: "Adverse Reaction", key: "adverseReaction" },
    { label: "Adverse Reaction Symptoms", key: "adverseReactionSymptoms" },
    {
      label: "Adverse Reaction Intervention",
      key: "adverseReactionIntervention",
    },
    { label: "Scan Timing", key: "tested" },
    { label: "Consumption Location", key: "consumptionLocation" },
    { label: "Behaviour Change", key: "behaviourChange" },
    { label: "Consumption Method", key: "consumptionMethod" },
    { label: "Notes", key: "notes" },
    { label: "Other Analytical Results", key: "otherResults" },
  ],
};

// Prevent CSV/Excel formula injection by prefixing dangerous leading characters.
function sanitizeCsvField(value) {
  if (typeof value === "string" && /^[=+\-@\t\r]/.test(value)) {
    return `\t${value}`;
  }
  return value;
}

export default function RecentSamples() {
  const [dateRange, setDateRange] = useState({
    start: DateTime.now().minus({ days: 6 }).toISODate(),
    end: DateTime.now().toISODate(),
  });
  const [selectedLocation, setSelectedLocation] = useState("All");

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20; // Number of rows per page

  //contexts
  const { selectedCustomer, customers } = useAdminContext();
  const { getColour, getTexture, getAdverse } = useParamsContext();
  const { user } = useUserContext();
  const customerType = user?.customerType;
  const locations = user?.locID;

  //fetch recent samples
  const {
    data: recentSamples,
    isLoading,
    isSuccess,
    isError,
  } = useRecentSamples(dateRange, selectedLocation, selectedCustomer);

  let tableData =
    isLoading || isError
      ? []
      : recentSamples.sequences.map((sample) => {
          let newItem = {};

          if (user.role === "admin") {
            newItem.customer =
              customers?.find((c) => c.customerId === sample.customerId)
                ?.customer || "N/A";
          }

          //get colour
          if (sample.colourId) {
            let c = getColour(sample.colourId);
            newItem.colour = c;
            if (c?.id.includes("02") && c?.label !== "Light Grey") {
              newItem.colourName = `Light ${c?.label}`; //adding "light" in front of colour label for light colours
            } else {
              newItem.colourName = c?.label;
            }
          }

          //get texture
          if (sample.texture && Object.keys(sample.texture).length !== 0) {
            let texture = getTexture(sample.texture);
            newItem.texture = texture;
            if (texture.label === "Other") {
              newItem.textureName = sanitizeCsvField(`${texture.label} - ${sample.texture.description}`);
            } else {
              newItem.textureName = texture.label;
            }
          } else {
            newItem.textureName = undefined;
            newItem.texture = undefined;
          }

          let localDate = DateTime.fromISO(sample.processedDate).setZone(
            "local",
          );

          newItem.processedDate = localDate.toFormat("DDD");

          newItem.time = localDate.toFormat("t");

          newItem.bulkScanFindings = sample.tests
            .filter((test) => test.scanType.toLowerCase() === "bulk-burst")
            .map((t) =>
              t.rb_findings && t.rb_findings.length > 0
                ? t.rb_findings.map((finding) => finding.substance)
                : t.findings.map((finding) => finding.substance),
            )
            .flat();

          newItem.bulkScanExport = newItem.bulkScanFindings.join(", ");

          newItem.traceScanFindings = sample.tests
            .filter((test) => test.scanType.toLowerCase() === "trace-burst")
            .map((t) =>
              t.rb_findings && t.rb_findings.length > 0
                ? t.rb_findings.map((finding) => finding.substance)
                : t.findings.map((finding) => finding.substance),
            )
            .flat();

          newItem.traceScanExport = newItem.traceScanFindings.join(", ");

          newItem._id = sample._id;
          newItem.sampleId = sample.sampleId;
          newItem.customId = sanitizeCsvField(sample.customId);

          const location = locations?.find(
            (location) => location.locationId === sample.locationId,
          );

          user.role === "admin"
            ? (newItem.location =
                customers
                  .find((c) => c.customerId === sample.customerId)
                  ?.locations?.find(
                    (loc) => loc.locationId === sample.locationId,
                  )?.name || sample.locationId)
            : (newItem.location = location ? location.name : sample.locationId);

          newItem.presumedSubstance = sanitizeCsvField(
            sample.presumedSubstance === "Other"
              ? `${sample.presumedSubstance} - ${sample.presumedSubDescription}`
              : sample.presumedSubstance,
          );

          newItem.notes = sanitizeCsvField(sample.description);
          newItem.reachbackStatus = sample.reachbackComplete;

          //Post consumption parameters (only for harm reduction customers)
          if (customerType === "harm-reduction") {
            newItem.adverseReaction =
              (sample.categories?.length > 0 && !sample.postConsumption) ||
              sample.postConsumption?.adverse
                ? "Yes"
                : "No";

            newItem.tested = sample.postConsumption?.tested || "N/A";
            newItem.consumptionLocation =
              sample.postConsumption?.consumptionLocation || "N/A";
            newItem.behaviourChange = sample.postConsumption?.behaviourChange
              ? sample.postConsumption.behaviourChange
                  .map((item) => item.label)
                  .join(", ")
              : "N/A";
            newItem.consumptionMethod = sample.postConsumption
              ?.consumptionMethod
              ? sample.postConsumption.consumptionMethod.label
              : "N/A";
            newItem.adverseReactionSymptoms =
              sample.postConsumption?.symptoms?.length > 0 &&
              sample.postConsumption?.adverse === true
                ? sample.postConsumption.symptoms
                    .map((symptom) => symptom.label)
                    .join(", ")
                : "N/A";
            newItem.adverseReactionIntervention =
              sample.postConsumption?.intervention?.length > 0 &&
              sample.postConsumption?.adverse === true
                ? sample.postConsumption.intervention
                    .map((intervention) => intervention.label)
                    .join(", ")
                : sample.categories?.length > 0
                  ? getAdverse(sample.categories).join(", ")
                  : "N/A";
          }

          newItem.otherResults = sanitizeCsvField(
            sample.otherResults && sample.otherResults.length > 0
              ? sample.otherResults
                  .map((result) => {
                    if (result.testType === "testStrip") {
                      return `${result.type} Test Strip: ${result.results}`;
                    } else if (result.type === "Other") {
                      return `${result.type} - ${result.otherTechniqueType}: ${result.results}`;
                    } else {
                      return `${result.type}: ${result.results}`;
                    }
                  })
                  .join(", ")
              : "N/A",
          );
          return newItem;
        });

  useEffect(() => {
    if (locations && locations.length > 1) {
      setSelectedLocation("All");
    } else if (locations && locations.length === 1 && user.role !== "admin") {
      setSelectedLocation(locations[0].locationId);
    }
  }, [locations, user.role]);

  useEffect(() => {
    if (isSuccess && recentSamples?.sequences.length > 0) {
      setCurrentPage(1); // Reset to first page when new data is loaded
    }
  }, [isSuccess, recentSamples?.sequences.length, dateRange, selectedLocation]);

  // Pagination logic
  const totalRows = tableData.length;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = tableData.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div
            className='alert  mx-3'
            role='alert'
            style={{ backgroundColor: "#7ECBF7", fontWeight: "bold" }}
          >
            Fetching samples ...
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex-container'>
        <Navbar signedIn={false} />
        <div
          className='px-sm-3 px-lg-5 py-3 flex-body'
          style={{ marginTop: "79px" }}
        >
          <div className='alert alert-danger mx-3' role='alert'>
            Something went wrong, try refreshing the page
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className='flex-container'>
      <Navbar signedIn={false} />
      <div
        className='px-sm-3 px-lg-5 py-3 flex-body'
        style={{ marginTop: "79px" }}
      >
        {isSuccess && (
          <CardA
            title='Recent Samples'
            subtitle='Click on a row to view and edit the sample.'
          >
            <div className='d-flex mb-3 justify-content-between align-items-end'>
              <div className='d-flex align-items-center gap-2'>
                <DateSelector
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                />
                {user.role === "admin"
                  ? selectedCustomer.locations?.length > 1 && (
                      <LocationDropdown
                        locations={selectedCustomer?.locations || []}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={setSelectedLocation}
                      />
                    )
                  : locations?.length > 1 && ( //only show LocationDropdown if there are multiple locations
                      <LocationDropdown
                        locations={locations}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={setSelectedLocation}
                      />
                    )}
              </div>
              <CSVLink
                data={tableData}
                headers={
                  user.role === "admin"
                    ? headers["admin"]
                    : headers[customerType]
                }
                filename={`Samples ${dateRange.start} to ${dateRange.end}.csv`}
                className='btn btn-outline-primary h-100'
              >
                Export to CSV
              </CSVLink>
            </div>
            {paginatedData.length === 0 ? (
              <div className='d-flex justify-content-center align-items-center flex-column flex-grow-1'>
                <i className='bi bi-search fs-1'></i>
                <h5>No samples found</h5>
                <h6>Please adjust the filter settings.</h6>
              </div>
            ) : (
              <div className='table-responsive flex-grow-1'>
                <table className='table table-hover'>
                  <thead>
                    <tr className='text-center'>
                      {user.role === "admin" && (
                        <>
                          <th scope='col' className='p-1'>
                            Customer
                          </th>
                          <th scope='col' className='p-1'>
                            Location
                          </th>
                        </>
                      )}
                      <th scope='col' className='p-1'>
                        Sample ID
                      </th>
                      <th scope='col' className='p-1'>
                        Custom ID
                      </th>
                      <th scope='col' className='p-1'>
                        Date
                      </th>
                      <th scope='col' className='p-1'>
                        Time
                      </th>
                      <th scope='col' className='p-1'>
                        Presumed Substance
                      </th>
                      <th scope='col' className='p-1'>
                        Colour
                      </th>
                      <th scope='col' className='p-1'>
                        Texture
                      </th>
                      <th scope='col' className='p-1'>
                        Bulk Scan
                      </th>
                      <th scope='col' className='p-1'>
                        Trace Scan
                      </th>
                      <th scope='col' className='p-1'>
                        Reachback Status
                      </th>
                      {customerType === "harm-reduction" && (
                        <th scope='col' className='p-1'>
                          Adverse Reaction
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((sample) => (
                      <tr
                        key={sample._id}
                        onClick={() =>
                          (window.location = `/sample/${sample._id}`)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {user.role === "admin" && (
                          <>
                            <td className='text-center align-middle'>
                              {sample.customer}
                            </td>
                            <td className='text-center align-middle'>
                              {sample.location}
                            </td>
                          </>
                        )}
                        <td className='text-center align-middle'>
                          {sample.sampleId}
                        </td>
                        <td className='text-center align-middle'>
                          {sample.customId || "-"}
                        </td>
                        <td className='text-center align-middle'>
                          {sample.processedDate}
                        </td>
                        <td className='text-center align-middle'>
                          {sample.time}
                        </td>
                        <td className='text-center align-middle'>
                          {sample.presumedSubstance
                            ? sample.presumedSubstance.includes("Fentalog")
                              ? sample.presumedSubstance.split(" ")[0]
                              : sample.presumedSubstance.includes(
                                    "Benzodiazepines",
                                  )
                                ? sample.presumedSubstance.split(" ")[0]
                                : sample.presumedSubstance
                            : "n/a"}
                        </td>
                        <td className='text-center align-middle'>
                          {sample.colour && sample.colour.id ? (
                            <ColourCircleC data={getColour(sample.colour.id)} />
                          ) : (
                            "n/a"
                          )}
                        </td>
                        <td className='text-center align-middle'>
                          {sample.texture && sample.texture.id ? (
                            <CardC data={getTexture(sample.texture)} />
                          ) : (
                            "n/a"
                          )}
                        </td>
                        <td className='align-middle'>
                          {
                            <SequenceD
                              findings={
                                sample.bulkScanFindings || ["Inconclusive"]
                              }
                            />
                          }
                        </td>

                        <td className='align-middle'>
                          {
                            <SequenceD
                              findings={
                                sample.traceScanFindings || ["Inconclusive"]
                              }
                            />
                          }
                        </td>

                        <td className='text-center align-middle'>
                          {sample.reachbackStatus === "N/A"
                            ? "n/a"
                            : sample.reachbackStatus === "inProgress"
                              ? "In Progress"
                              : "Complete"}
                        </td>

                        {customerType === "harm-reduction" && (
                          <td className='text-center align-middle'>
                            {sample.adverseReaction}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  totalRows={totalRows}
                  pageSize={pageSize}
                />
              </div>
            )}
          </CardA>
        )}
      </div>
      <Footer />
    </div>
  );
}
